import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import Header from '../components/layouts/Header'
import Footer from '../components/layouts/Footer'
import Button from '../components/general/Button'
import useCanonical from '../hooks/useCanonical'
import { ROUTES } from '../routes/RouteList'
import { getStructuredDataBaseUrl } from '../utils/canonical'

const PageNotFound = () => {
  const canonicalTag = useCanonical('/404');
  
  return (
    <>
      {canonicalTag}
      <Helmet>
        <title>404 - Page Not Found | Dev-Hire</title>
        <meta name="description" content="The page you're looking for doesn't exist. Find top developers or explore our hiring platform at Dev-Hire." />
        <meta name="robots" content="noindex, nofollow" />
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "404 - Page Not Found",
            "description": "The page you're looking for doesn't exist. Find top developers or explore our hiring platform at Dev-Hire.",
            "url": "${getStructuredDataBaseUrl()}/404",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Dev-Hire",
              "url": "${getStructuredDataBaseUrl()}"
            }
          }`}
        </script>
      </Helmet>
      
      <Header />
      
      <main className="bg-black min-h-screen text-white">
        <div className="--layout-padding max-width">
          {/* Hero Section */}
          <div className="flex flex-col items-center justify-center min-h-[70vh] text-center py-20">
            {/* 404 Number */}
            <div className="mb-8">
              <h1 className="text-[120px] md:text-[80px] lg:text-[100px] font-bold text-primary-color leading-none">
                404
              </h1>
            </div>
            
            {/* Error Message */}
            <div className="mb-8 max-w-2xl">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-4 text-center">
                Oops! Page Not Found
              </h2>
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed text-center">
                The page you're looking for seems to have vanished into the digital void. 
                Don't worry though - we've got plenty of amazing developers waiting to help you build something great!
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                className="bg-primary-color text-black rounded px-8 py-4 text-lg font-semibold hover:bg-[#d65a2e] transition-colors duration-300"
                href={ROUTES.HOME}
              >
                Go to Homepage
              </Button>
              <Button 
                className="border border-primary-color text-primary-color rounded px-8 py-4 text-lg font-semibold hover:bg-primary-color hover:text-black transition-all duration-300"
                href={ROUTES.START_HIRING}
              >
                Start Hiring
              </Button>
            </div>
          </div>
          
          {/* Helpful Links Section */}
          <div className="py-16 border-t border-[#292929]">
            <div className="text-center mb-12">
              <h3 className="text-2xl md:text-3xl font-semibold mb-4 text-center">
                Let's Get You Back on Track
              </h3>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto text-center">
                Here are some popular destinations to help you find what you're looking for:
              </p>
            </div>
            
            {/* Navigation Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {/* Hire Developers Card */}
              <div className="bg-[#1a1a1a] border border-[#292929] rounded-lg p-6 hover:border-primary-color transition-colors duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-color rounded-lg flex items-center justify-center mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-black">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold">Hire Developers</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Find top 3% vetted developers across React, Python, AWS, and more technologies.
                </p>
                <Link 
                  to={ROUTES.START_HIRING}
                  className="text-primary-color hover:text-[#d65a2e] font-medium flex items-center group"
                >
                  Start Hiring Now
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
              
              {/* Find Jobs Card */}
              <div className="bg-[#1a1a1a] border border-[#292929] rounded-lg p-6 hover:border-primary-color transition-colors duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-color rounded-lg flex items-center justify-center mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-black">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold">Find Jobs</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Join as a developer and get matched with top companies looking for your skills.
                </p>
                <Link 
                  to={ROUTES.FIND_JOBS}
                  className="text-primary-color hover:text-[#d65a2e] font-medium flex items-center group"
                >
                  Join as Developer
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
              
              {/* About Us Card */}
              <div className="bg-[#1a1a1a] border border-[#292929] rounded-lg p-6 hover:border-primary-color transition-colors duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-color rounded-lg flex items-center justify-center mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-black">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold">About Dev-Hire</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Learn more about our mission to connect top developers with amazing companies.
                </p>
                <Link 
                  to={ROUTES.ABOUT_US}
                  className="text-primary-color hover:text-[#d65a2e] font-medium flex items-center group"
                >
                  Learn More
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
              
              {/* Blog Card */}
              <div className="bg-[#1a1a1a] border border-[#292929] rounded-lg p-6 hover:border-primary-color transition-colors duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-color rounded-lg flex items-center justify-center mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-black">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold">Our Blog</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Read insights about hiring, development trends, and industry best practices.
                </p>
                <a 
                  href="https://blog.dev-hire.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-color hover:text-[#d65a2e] font-medium flex items-center group"
                >
                  Read Articles
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </a>
              </div>
              
              {/* Contact Us Card */}
              <div className="bg-[#1a1a1a] border border-[#292929] rounded-lg p-6 hover:border-primary-color transition-colors duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-color rounded-lg flex items-center justify-center mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-black">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold">Contact Us</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Have questions? Get in touch with our team for personalized assistance.
                </p>
                <Link 
                  to={ROUTES.CONTACT_US}
                  className="text-primary-color hover:text-[#d65a2e] font-medium flex items-center group"
                >
                  Get in Touch
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
              
              {/* Login Card */}
              <div className="bg-[#1a1a1a] border border-[#292929] rounded-lg p-6 hover:border-primary-color transition-colors duration-300">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-color rounded-lg flex items-center justify-center mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-black">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-semibold">Login</h4>
                </div>
                <p className="text-gray-300 mb-4">
                  Access your account to manage your hiring or job search activities.
                </p>
                <Link 
                  to={ROUTES.LOGIN_REGISTER}
                  className="text-primary-color hover:text-[#d65a2e] font-medium flex items-center group"
                >
                  Sign In
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  )
}

export default PageNotFound;
